import { Suspense } from 'react';
import Experience from './Experience';
import { Canvas, useThree } from '@react-three/fiber';
import { Route, useLocation, useRoute } from "wouter"
import { Loader } from '@react-three/drei';
import Alert from './components/Alert';
import AssetLoader from './sceneSetUp/AssetLoader';
import AudioController from './soundPlayers/AudioController';
import MusicController from './soundPlayers/MusicController';
import Home2 from './Home2';
import RulebookText from './text/RulebookText';
import { useWindowSize } from './hooks/useWindowSize';
import React from 'react';
import StructuredData from "./seo/StructuredData";
import rulebookSchema from "./seo/rulebookSchema";
import rootSchema from "./seo/rootSchema";
import SEO from "./seo/SEO";
import HowToPlayHome from './components/HowToPlayHome';
import RulebookTextKorean from './text/RulebookTextKorean';
import Blog from './components/blog/Blog';
import Backdrop from './components/Backdrop';
import PrivacyPolicy from './components/privacy/PrivacyPolicy';

// Component to handle resize and pixel ratio updates
function ResizeHandler() {
  const { gl, camera } = useThree();
  
  // Call on mount AND on resize
  useWindowSize((sizes) => {
    camera.aspect = sizes.width / sizes.height;
    camera.updateProjectionMatrix();
    
    gl.setSize(sizes.width, sizes.height);
    gl.setPixelRatio(sizes.pixelRatio);
  });
  
  // Also run immediately on mount
  React.useEffect(() => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    gl.setSize(window.innerWidth, window.innerHeight);
    gl.setPixelRatio(window.devicePixelRatio);
  }, [gl, camera]);
  
  return null;
}

export default function App () {

  const created = ({ gl }) => {
    gl.setClearColor('#090f16', 1)

    // for shader
    const sizes = {
      width: window.innerWidth,
      height: window.innerHeight,
      pixelRatio: window.devicePixelRatio
    }
    gl.setSize(sizes.width, sizes.height)
    gl.setPixelRatio(sizes.pixelRatio)
  }

  const [isRulebookRoute] = useRoute("/how-to-play");
  const [isHomeRoute] = useRoute("/");
  const [isAboutRoute] = useRoute("/about");
  const [isPrivacyRoute] = useRoute("/privacy");
  const [location] = useLocation();
  const isBlogRoute = location === '/blog' || location.startsWith('/blog/');

  return (<>
    {/* Dynamic SEO based on route */}
    {isHomeRoute && (
      <>
        <StructuredData data={rootSchema} />
        <SEO 
          title="Yutnori (윷놀이) - Play Free Online 3D Korean Board Game"
          description="Play Yutnori (윷놀이) free online! Experience the traditional Korean board game in stunning 3D. Throw the yut sticks, capture opponents, and race to victory. Play with friends or AI - no download needed."
          keywords="yutnori, 윷놀이, korean board game, yut, traditional korean game, online board game, multiplayer game, 3D board game, free online game, korean culture, strategy game, yut sticks, yunnori, yoot"
          canonical="https://yutnori.app/"
        />
      </>
    )}
    {isRulebookRoute && (
      <>
        <StructuredData data={rulebookSchema} />
        <SEO 
          title="How to Play Yutnori (윷놀이) - Complete Rules & Strategy Guide"
          description="Learn how to play Yutnori (윷놀이), the traditional Korean board game. Step-by-step guide with 3D animations covering yut stick throws, movement, capturing, stacking, and winning strategies."
          keywords="yutnori rules, how to play yutnori, 윷놀이 rules, yut game instructions, korean board game rules, yutnori tutorial, yut sticks explained, yutnori strategy"
          canonical="https://yutnori.app/how-to-play"
        />
      </>
    )}
    {isAboutRoute && (
      <SEO 
        title="About Yutnori - Traditional Korean Board Game History"
        description="Discover the history and cultural significance of Yutnori (윷놀이), a traditional Korean board game played for over 1000 years. Learn about this digital preservation of Korean heritage."
        keywords="yutnori history, korean culture, traditional games, 윷놀이 history, korean heritage, cultural games, asian board games"
        canonical="https://yutnori.app/about"
      />
    )}
    {isPrivacyRoute && (
      <SEO
        title="Privacy Policy | Yut Game"
        description="Privacy Policy for Yut Game."
        canonical="https://yutnori.app/privacy"
      />
    )}
    
    {!isBlogRoute && <Canvas
      className='r3f'
      onCreated={ created }
    >
      <Suspense fallback={null}>
        <ResizeHandler />
        {/* <Perf/> */}
        <directionalLight castShadow position={ [ 1, 6, 3 ] } intensity={ 4 } />
        <ambientLight intensity={ 1.5 } />
        {/* <ParticleSystem/> */}
        <AssetLoader/>
        <Route path="/">
          <Home2/>
        </Route>
        <Route path="/how-to-play">
          <HowToPlayHome/>
        </Route>
        <Route path="/game/:id">
          <Experience/>
          <Alert position={[0, 3, 0.5]} rotation={[0,0,0]}/>
        </Route>
      </Suspense>
      <AudioController/>
      <MusicController/>
    </Canvas>}
    <Route path="/blog">
      <Blog/>
    </Route>
    <Route path="/blog/howtoplay">
      <Blog/>
    </Route>
    <Route path="/blog/updates">
      <Blog/>
    </Route>
    <Route path="/blog/metrics">
      <Blog/>
    </Route>
    <Route path="/privacy">
      <PrivacyPolicy/>
    </Route>
    {!isBlogRoute && <>
      <Loader/>
      <RulebookText/>
    </>}
    
    {/* Hidden HTML content for /how-to-play route - SEO purposes */}
    {isRulebookRoute && (
      <div style={{ 
        position: 'absolute', 
        left: '-9999px', 
        width: '1px', 
        height: '1px', 
        overflow: 'hidden',
        clip: 'rect(0,0,0,0)',
        whiteSpace: 'nowrap'
      }}>
        <RulebookText/>
        <RulebookTextKorean/>
      </div>
    )}
  </>)
}